import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import {
  FitnessCenter,
  TrackChanges,
  Psychology,
  SportsScore,
  Timer,
  MenuBook,
  AutoStories,
  ExpandMore,
  ExpandLess,
  FormatListNumbered,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ip_back } from "../../config";
import { exdetails } from "../funtionsBD/Mainservice";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import {
  blockTypeHeadline,
  exerciseLabel,
  exerciseMinutes,
  isTypeOnlyBlock,
  sequenceCount,
} from "../../utils/planningSubgroups";
import { localizedField, localizedTypeName } from "../../utils/localizedCatalogFields";
import { currentAppLanguage } from "../../utils/appLanguages";
import { formatDurationLabel } from "../../utils/trainingDuration";
import "../PlayerSessionResultsDialog.css";
import "../../pages/Exerciseadd/Exercise.css";
import "../../pages/coach/ExerciseDetails.css";
import "./PlanningExerciseDetailsDialog.css";

const LEVEL_COLORS = [
  "#4caf50", "#8bc34a", "#cddc39", "#ffc107", "#ff9800",
  "#ff5722", "#e91e63", "#9c27b0", "#673ab7", "#3f51b5",
];

const CardioIndicator = ({ level }) => {
  if (!level && level !== 0) return null;
  const maxLevel = 5;
  const filled = Math.min(level, maxLevel);
  return (
    <div className="excard-cardio">
      {Array.from({ length: maxLevel }, (_, i) => (
        <span
          key={i}
          className={`excard-cardio__dot ${i < filled ? "excard-cardio__dot--filled" : ""}`}
          style={
            i < filled
              ? { backgroundColor: LEVEL_COLORS[Math.min(i, LEVEL_COLORS.length - 1)] }
              : {}
          }
        />
      ))}
    </div>
  );
};

const ThemeBadge = ({ theme, lng }) => {
  const name = localizedField(theme, "theme_name", lng) || "—";
  const level = theme.level || theme.the_lavel || 0;
  const color = LEVEL_COLORS[Math.min(level - 1, LEVEL_COLORS.length - 1)] || "#90a4ae";

  return (
    <span className="excard-badge excard-badge--theme" style={{ borderColor: color }}>
      <span className="excard-badge__label">{name}</span>
      <span className="excard-badge__level" style={{ backgroundColor: color }}>
        L{level}
      </span>
    </span>
  );
};

const DetailSection = ({ icon: Icon, iconColor, title, children, collapsible }) => {
  const [open, setOpen] = useState(!collapsible);

  if (!children) return null;

  return (
    <div className="exdetail-section">
      <div
        className={`exdetail-section__header ${collapsible ? "exdetail-section__header--clickable" : ""}`}
        onClick={collapsible ? () => setOpen((p) => !p) : undefined}
      >
        <Icon className="exdetail-section__icon" style={iconColor ? { color: iconColor } : {}} />
        <span className="exdetail-section__title">{title}</span>
        {collapsible && (
          open ? (
            <ExpandLess className="exdetail-section__chevron" />
          ) : (
            <ExpandMore className="exdetail-section__chevron" />
          )
        )}
      </div>
      {open && <div className="exdetail-section__body">{children}</div>}
    </div>
  );
};

export function PlanningExerciseDetailsDialog({
  open,
  exercise,
  stationName,
  sessionLabel,
  canEdit,
  onClose,
  onEdit,
  onDelete,
  onSwap,
  onSyncBlock,
  /** Sync only makes sense with 2+ stations/subgroups. */
  canSyncBlock = true,
}) {
  const { t, i18n } = useTranslation();
  const lng = currentAppLanguage(i18n);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open || !exercise?.te_id) {
      setDetails(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const response = await exdetails({ te_id: exercise.te_id });
        if (!cancelled) setDetails(response?.data?.data || null);
      } catch {
        if (!cancelled) setDetails(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, exercise?.te_id]);

  const info = details || {};
  const name =
    exerciseLabel(exercise, lng) ||
    localizedField(info, "ex_name", lng) ||
    t("trainingBlock");
  const objective = localizedField(info, "ex_objective", lng);
  const instruction = localizedField(info, "ex_instruction", lng);
  const process = localizedField(info, "ex_process", lng);
  const storytelling = localizedField(info, "ex_storytelling", lng);
  const mins = exerciseMinutes(exercise) || info?.te_duration;
  const seqs = Array.isArray(exercise?.sequences)
    ? [...exercise.sequences].sort((a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0))
    : [];

  const typeOnly = isTypeOnlyBlock(exercise);
  const typeName =
    localizedTypeName(info.type_details, lng) ||
    localizedTypeName(exercise?.type_ex || exercise?.tbl_type_ex, lng) ||
    "";
  const isBlocksOfCompetency =
    (info.type_details?.type_name_en ||
      exercise?.type_ex?.type_name_en ||
      "") === "Blocks of competency";
  const cbName =
    localizedField(info.cb_details, "cb_name", lng) ||
    localizedField(exercise?.competency_block, "cb_name", lng) ||
    localizedField(exercise?.ex_sub_type_id, "cb_name", lng) ||
    blockTypeHeadline(exercise, lng) ||
    "";
  const subTypeName =
    isBlocksOfCompetency && cbName
      ? cbName
      : localizedField(info.sub_type_details, "sub_type_name", lng) ||
        localizedField(exercise?.sub_type_ex, "sub_type_name", lng) ||
        (typeOnly ? blockTypeHeadline(exercise, lng) : "") ||
        "";

  const themes = info.themes || [];
  const skills = info.skills || [];
  const focusList = info.focus || [];
  const cardioLev = info.ex_cardio_lev;
  const subTypeImage =
    info.sub_type_details?.sub_type_image_normal ||
    exercise?.sub_type_ex?.sub_type_image_normal;
  const subtitleBits = [stationName, sessionLabel].filter(Boolean);

  if (!open || !exercise) return null;

  const emptyBlock =
    !loading &&
    !details &&
    sequenceCount(exercise) === 0 &&
    !process &&
    !objective &&
    !instruction;

  return createPortal(
    <div
      className="player-session-results__backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="player-session-results__sheet psg-exdetail-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={name}
      >
        <div className="player-session-results__handle" />
        <button
          type="button"
          className="player-session-results__close"
          onClick={onClose}
          aria-label={t("close") || "Close"}
        >
          <CloseIcon />
        </button>

        <div className="player-session-results__content">
          <div className="player-session-results__header">
            <button
              type="button"
              className="player-session-results__back-btn player-session-results__back-btn--ios-nav"
              onClick={onClose}
            >
              <ArrowBackIcon fontSize="small" />
              <span>{t("back") || "Back"}</span>
            </button>
            <div className="player-session-results__header-new">
              <div className="player-session-results__header-text">
                <h2 className="player-session-results__title-new">
                  {t("exdetails") || "Exercise details"}
                </h2>
                {subtitleBits.length > 0 ? (
                  <p className="player-session-results__subtitle-new">
                    {subtitleBits.join(" · ")}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="player-session-results__body psg-exdetail-body">
            {loading && !details ? (
              <p className="psg-exdetail-loading">{t("loading") || "Loading…"}</p>
            ) : (
              <div className="exdetail-page psg-exdetail-page">
                <div className="exdetail-card">
                  <div className="exdetail-hero">
                    {subTypeImage ? (
                      <div className="exdetail-hero__img">
                        <img src={ip_back + subTypeImage} alt="" />
                      </div>
                    ) : null}
                    <div className="exdetail-hero__info">
                      <h1 className="exdetail-hero__name">{name}</h1>
                      <div className="exdetail-hero__tags">
                        {typeName ? (
                          <span className="excard-tag excard-tag--type">{typeName}</span>
                        ) : null}
                        {subTypeName ? (
                          <span
                            className={`excard-tag ${
                              isBlocksOfCompetency && cbName
                                ? "excard-tag--cb"
                                : "excard-tag--subtype"
                            }`}
                            title={
                              isBlocksOfCompetency && cbName
                                ? t("competencyblock") || "Competency Block"
                                : undefined
                            }
                          >
                            {subTypeName}
                          </span>
                        ) : null}
                        {info.isTerminal === "Yes" ? (
                          <span className="excard-tag excard-tag--subtype">BC</span>
                        ) : null}
                        {cardioLev != null ? (
                          <span
                            className="excard-tag excard-tag--cardio"
                            title={t("cardioLevel")}
                          >
                            <FitnessCenter style={{ fontSize: 13 }} />
                            <CardioIndicator level={cardioLev} />
                          </span>
                        ) : null}
                      </div>
                      <div className="exdetail-hero__meta">
                        {mins ? (
                          <span className="exdetail-hero__duration">
                            <Timer style={{ fontSize: 16 }} />
                            {formatDurationLabel(mins) || `${mins} ${t("min")}`}
                          </span>
                        ) : null}
                        {info.te_state ? (
                          <span className="exdetail-hero__state">
                            {localizedField(info.te_state, "st_name", lng)}
                          </span>
                        ) : null}
                      </div>
                      {process ? (
                        <p className="exdetail-hero__description">{process}</p>
                      ) : null}
                    </div>
                  </div>

                  {themes.length > 0 ? (
                    <div className="exdetail-badges-row">
                      <TrackChanges
                        className="exdetail-badges-row__icon"
                        style={{ color: "var(--brand-primary, #5c6bc0)" }}
                      />
                      <span className="exdetail-badges-row__label">{t("themes")}</span>
                      <div className="exdetail-badges-row__list">
                        {themes.map((theme, idx) => (
                          <ThemeBadge key={idx} theme={theme} lng={lng} />
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {skills.length > 0 ? (
                    <div className="exdetail-badges-row">
                      <Psychology
                        className="exdetail-badges-row__icon"
                        style={{ color: "var(--brand-secondary, #26a69a)" }}
                      />
                      <span className="exdetail-badges-row__label">{t("skills")}</span>
                      <div className="exdetail-badges-row__list">
                        {skills.map((skill, idx) => (
                          <span key={idx} className="excard-badge excard-badge--skill">
                            {localizedField(skill, "skill_name", lng) || "—"}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="exdetail-sections">
                  {objective ? (
                    <DetailSection
                      icon={SportsScore}
                      iconColor="#1565c0"
                      title={t("objective")}
                    >
                      <p className="exdetail-text">{objective}</p>
                    </DetailSection>
                  ) : null}

                  {focusList.length > 0 ? (
                    <DetailSection
                      icon={TrackChanges}
                      iconColor="#e65100"
                      title={t("focus")}
                    >
                      {focusList.map((f, idx) => {
                        const focusText =
                          localizedField(f, "focus_description", lng) || "—";
                        return (
                          <p
                            key={idx}
                            className="exdetail-text exdetail-text--focus-item"
                          >
                            {focusText}
                          </p>
                        );
                      })}
                    </DetailSection>
                  ) : null}

                  {instruction ? (
                    <DetailSection
                      icon={MenuBook}
                      iconColor="#0877BC"
                      title={t("instruction")}
                    >
                      <p className="exdetail-text">{instruction}</p>
                    </DetailSection>
                  ) : null}

                  {storytelling ? (
                    <DetailSection
                      icon={AutoStories}
                      iconColor="#6a1b9a"
                      title={t("storytelling") || "Storytelling"}
                      collapsible
                    >
                      <p className="exdetail-text">{storytelling}</p>
                    </DetailSection>
                  ) : null}

                  {seqs.length > 0 ? (
                    <DetailSection
                      icon={FormatListNumbered}
                      iconColor="#0877BC"
                      title={t("sequences") || "Sequences"}
                    >
                      <ol className="psg-exdetail-seqs">
                        {seqs.map((seq, idx) => (
                          <li key={seq.tes_id || idx}>
                            <button
                              type="button"
                              className="psg-exdetail-seq-btn"
                              disabled={!canEdit}
                              onClick={() => canEdit && onEdit?.(exercise)}
                            >
                              <span>
                                {localizedField(seq.sub_type_ex, "sub_type_name", lng) ||
                                  `${t("sequence") || "Sequence"} ${
                                    seq.sequence_order || idx + 1
                                  }`}
                              </span>
                              <span>
                                {formatDurationLabel(seq.sequence_duration) ||
                                  `${seq.sequence_duration || "—"} ${t("min") || "min"}`}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ol>
                    </DetailSection>
                  ) : null}
                </div>

                {emptyBlock || typeOnly ? (
                  <p className="psg-exdetail-empty">
                    {typeOnly
                      ? t("typeOnlyBlockHint") ||
                        "Type is set from the template. Edit the type or competency block, then assign a catalogue exercise."
                      : t("emptyBlockHint") ||
                        "Pick an exercise or template for this block."}
                  </p>
                ) : null}
              </div>
            )}
          </div>

          <footer className="psg-exdetail-actions">
            {canEdit ? (
              <>
                {onSwap ? (
                  <button
                    type="button"
                    className="psg-exdetail-btn psg-exdetail-btn--ghost"
                    onClick={() => onSwap?.(exercise)}
                  >
                    <SwapHorizIcon sx={{ fontSize: 18 }} />
                    {t("swapExercise") || "Swap block"}
                  </button>
                ) : null}
                {onSyncBlock && canSyncBlock && !emptyBlock ? (
                  <button
                    type="button"
                    className="psg-exdetail-btn psg-exdetail-btn--ghost"
                    onClick={() => onSyncBlock?.(exercise)}
                  >
                    <SyncAltIcon sx={{ fontSize: 18 }} />
                    {t("syncBlockExercise") || "Sync block"}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="psg-exdetail-btn psg-exdetail-btn--primary"
                  onClick={() => onEdit?.(exercise)}
                >
                  <EditOutlinedIcon sx={{ fontSize: 18 }} />
                  {typeOnly
                    ? t("editExerciseType") || "Edit type"
                    : t("editExercise") || "Edit"}
                </button>
                {onDelete ? (
                  <button
                    type="button"
                    className="psg-exdetail-btn psg-exdetail-btn--danger"
                    onClick={() => onDelete?.(exercise)}
                    aria-label={t("deleteTrainingBlock") || t("deleteExercise") || "Delete"}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </button>
                ) : null}
              </>
            ) : null}
          </footer>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default PlanningExerciseDetailsDialog;
